import React, { useRef, useState, useLayoutEffect } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { useLanguage } from "../../../contexts/LanguageContext";
import SectionHeading from "../SectionHeading";

/**
 * 3 lignes de 7 images, distinctes de TopListSection/NewDishSection, format
 * paysage ~4:3. 7 par ligne pour garantir une largeur de rangée toujours
 * supérieure à la largeur de l'écran (nécessaire pour que le défilement
 * horizontal ait une distance à parcourir sur les grands écrans).
 */
const ROW_1 = [
  { nameKey: "topDish1Name", img: "https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=900&h=650&fit=crop&q=85" },
  { nameKey: "topDish2Name", img: "https://images.unsplash.com/photo-1495521821757-a1efb6729352?w=900&h=650&fit=crop&q=85" },
  { nameKey: "topDish3Name", img: "https://images.unsplash.com/photo-1546833999-b9f581a1996d?w=900&h=650&fit=crop&q=85" },
  { nameKey: "topDish4Name", img: "https://images.unsplash.com/photo-1512621776951-a57141f2eefd?w=900&h=650&fit=crop&q=85" },
  { nameKey: "topDish5Name", img: "https://images.unsplash.com/photo-1476718406336-bb5a9690ee2a?w=900&h=650&fit=crop&q=85" },
  { nameKey: "topDish1Name", img: "https://images.unsplash.com/photo-1467003909585-2f8a72700288?w=900&h=650&fit=crop&q=85" },
  { nameKey: "topDish2Name", img: "https://images.unsplash.com/photo-1490645935967-10de6ba17061?w=900&h=650&fit=crop&q=85" },
];
const ROW_2 = [
  { nameKey: "topDish3Name", img: "https://images.unsplash.com/photo-1495195129352-aeb325a55b65?w=900&h=650&fit=crop&q=85" },
  { nameKey: "topDish4Name", img: "https://images.unsplash.com/photo-1567620905732-2d1ec7ab7445?w=900&h=650&fit=crop&q=85" },
  { nameKey: "topDish5Name", img: "https://images.unsplash.com/photo-1512058564366-18510be2db19?w=900&h=650&fit=crop&q=85" },
  { nameKey: "topDish1Name", img: "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=900&h=650&fit=crop&q=85" },
  { nameKey: "topDish2Name", img: "https://images.unsplash.com/photo-1551504734-5ee1c4a1479b?w=900&h=650&fit=crop&q=85" },
  { nameKey: "topDish3Name", img: "https://images.unsplash.com/photo-1571091718767-18b5b1457add?w=900&h=650&fit=crop&q=85" },
  { nameKey: "topDish4Name", img: "https://images.unsplash.com/photo-1481931098730-318b6f776db0?w=900&h=650&fit=crop&q=85" },
];
const ROW_3 = [
  { nameKey: "topDish5Name", img: "https://images.unsplash.com/photo-1476224203421-9ac39bcb3327?w=900&h=650&fit=crop&q=85" },
  { nameKey: "topDish1Name", img: "https://images.unsplash.com/photo-1478144592103-25e218a04891?w=900&h=650&fit=crop&q=85" },
  { nameKey: "topDish2Name", img: "https://images.unsplash.com/photo-1580476262798-bddd9f4b7369?w=900&h=650&fit=crop&q=85" },
  { nameKey: "topDish3Name", img: "https://images.unsplash.com/photo-1526318896980-cf78c088247c?w=900&h=650&fit=crop&q=85" },
  { nameKey: "topDish4Name", img: "https://images.unsplash.com/photo-1567337710282-00832b415979?w=900&h=650&fit=crop&q=85" },
  { nameKey: "topDish5Name", img: "https://images.unsplash.com/photo-1552566626-52f8b828add9?w=900&h=650&fit=crop&q=85" },
  { nameKey: "topDish1Name", img: "https://images.unsplash.com/photo-1490474418585-ba9bad8fd0ea?w=900&h=650&fit=crop&q=85" },
];

const GalleryTile = ({ image, t, sizeClass }) => (
  <div className={`relative shrink-0 aspect-[4/3] rounded-2xl overflow-hidden bg-ink-100 ${sizeClass}`}>
    <img
      src={image.img}
      alt={t(image.nameKey)}
      className="w-full h-full object-cover"
      loading="lazy"
      decoding="async"
    />
  </div>
);

/**
 * Galerie plein-écran sur 3 lignes : chaque ligne glisse horizontalement à
 * sa propre vitesse (effet parallax) pendant que la section reste épinglée
 * au scroll vertical (scrollytelling), sur desktop comme sur mobile. Seul
 * `prefers-reduced-motion` désactive l'effet, au profit de 3 bandes à
 * défilement tactile classique (pas de pin, pas d'auto-scroll).
 */
const ScrollGallerySection = () => {
  const { t } = useLanguage();
  const shouldReduceMotion = useReducedMotion();
  const shouldPin = !shouldReduceMotion;

  const trackRef = useRef(null);
  const row1Ref = useRef(null);
  const row2Ref = useRef(null);
  const row3Ref = useRef(null);
  const [distances, setDistances] = useState({ row1: 0, row2: 0, row3: 0 });

  useLayoutEffect(() => {
    if (!shouldPin) return;
    const measure = () => {
      const widthOf = (row) =>
        row.current ? Math.max(row.current.scrollWidth - window.innerWidth, 0) : 0;
      setDistances({
        row1: widthOf(row1Ref),
        row2: widthOf(row2Ref),
        row3: widthOf(row3Ref),
      });
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [shouldPin]);

  const { scrollYProgress } = useScroll({
    target: trackRef,
    offset: ["start start", "end end"],
  });

  /** Ligne 1 : rapide, vers la gauche. Ligne 2 : vers la droite (sens inverse). Ligne 3 : lente, vers la gauche. */
  const x1 = useTransform(scrollYProgress, [0, 1], [0, -distances.row1]);
  const x2 = useTransform(scrollYProgress, [0, 1], [-distances.row2, 0]);
  const x3 = useTransform(scrollYProgress, [0, 1], [0, -distances.row3 * 0.55]);

  /** En dvh sur mobile pour que les 3 lignes remplissent mieux la hauteur épinglée (moins de vide en haut/bas) */
  const tileSizePinned = "h-[26dvh] sm:h-[180px] lg:h-[210px]";
  const tileSizeMobile = "w-[70vw] sm:w-[50vw] max-w-[360px]";

  return (
    <section className="w-full min-w-0 py-12 sm:py-16 md:py-20" aria-label={t("galleryTitle")}>
      <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <SectionHeading
          index="06"
          kicker={t("galleryKicker")}
          title={t("galleryTitle")}
          className="sm:mb-12 md:mb-14"
        />
      </div>

      {shouldPin ? (
        <div ref={trackRef} style={{ height: "250dvh" }} className="relative">
          <div className="sticky top-0 h-dvh flex flex-col justify-center gap-4 sm:gap-5 overflow-hidden">
            <motion.div ref={row1Ref} style={{ x: x1 }} className="flex gap-4 sm:gap-5 pl-4 sm:pl-6 lg:pl-8">
              {ROW_1.map((image, i) => (
                <GalleryTile key={i} image={image} t={t} sizeClass={tileSizePinned} />
              ))}
            </motion.div>
            <motion.div ref={row2Ref} style={{ x: x2 }} className="flex gap-4 sm:gap-5 pl-4 sm:pl-6 lg:pl-8">
              {ROW_2.map((image, i) => (
                <GalleryTile key={i} image={image} t={t} sizeClass={tileSizePinned} />
              ))}
            </motion.div>
            <motion.div ref={row3Ref} style={{ x: x3 }} className="flex gap-4 sm:gap-5 pl-4 sm:pl-6 lg:pl-8">
              {ROW_3.map((image, i) => (
                <GalleryTile key={i} image={image} t={t} sizeClass={tileSizePinned} />
              ))}
            </motion.div>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {[ROW_1, ROW_2, ROW_3].map((row, rowIndex) => (
            <div
              key={rowIndex}
              className="overflow-x-auto overflow-y-hidden overscroll-x-none snap-x snap-mandatory scrollbar-hide px-4 sm:px-6 lg:px-8"
            >
              <div className="flex gap-4 sm:gap-5 py-1">
                {row.map((image, i) => (
                  <GalleryTile
                    key={i}
                    image={image}
                    t={t}
                    sizeClass={`snap-center snap-always ${tileSizeMobile}`}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default ScrollGallerySection;
